import { useState } from 'react';
import { RichText } from '../../richText/RichText';
import { Link } from '../../router/Link';
import { useNavigate } from '../../router/router';
import {
  Button,
  JumpOutIcon,
  LoadingStatus,
  Menu,
  Notice,
  ProblemPanel,
  Skeleton,
  Spinner,
  StatePanel,
} from '../../ui';
import { FeedGridSkeleton, ListingGrid } from '../../listing/FeedGrid';
import { GridSearch } from '../../listing/GridSearch';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { SubscribeButton } from '../../subscriptions/SubscribeButton';
import layout from '../../ui/layout.module.css';
import {
  channelClearSearchHref,
  channelSearchHref,
  sortHref,
  type ChannelTab,
  type ChannelVideoSort,
} from './model';
import type { MenuModel } from '../../ui/menuModel';
import styles from './channel.module.css';
import { useChannel, type ChannelBinding } from './useChannel';
import { useCopy } from '../../strings/useCopy';

/**
 * §8's channel page. Renders props (R13) — every string arrived formatted, every href arrived built,
 * and the only branch is on `state.kind`.
 *
 * A `<div>` rather than a `<main>`: the shell owns that landmark.
 */
export function ChannelView({
  channelId,
  tab,
  sort,
  search,
}: {
  readonly channelId: string;
  readonly tab: ChannelTab;
  readonly sort: ChannelVideoSort;
  /** The in-channel search — non-null, the body shows `…/search?q=` results, not a tab. */
  readonly search: { readonly q: string; readonly page: number } | null;
}) {
  const binding = useChannel(channelId, tab, sort, search);

  return (
    <div className={layout.wide}>
      <section className={styles.channel}>
        {/*
         * A failed channel gets its name and nothing else. Found in a driven browser: with the head
         * rendered unconditionally, `/channel/UCnotarealchannel` drew an empty avatar ring, an empty tab
         * strip, a divider and a live "Sort · Newest" menu above "No such channel" — chrome for a page
         * that does not exist, and a control that cannot do anything, which is §8's "shown broken".
         */}
        {binding.problem === null ? (
          <>
            <ChannelHead binding={binding} channelId={channelId} search={search?.q ?? null} />
            <TabBody binding={binding} search={search?.q ?? null} />
          </>
        ) : (
          <>
            <FailedHead channelId={channelId} />
            <ChannelProblem binding={binding} />
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The in-channel search control — the grids' collapsible icon, submitting a navigation: the
 * query is a place with a URL, so the answer has to be shareable and Back has to be what "stop
 * searching" means. Keyed by the route's `q` from the caller, so arriving at a search page seeds
 * the box and a new search reseeds it.
 */
function ChannelSearchControl({ channelId, q }: { readonly channelId: string; readonly q: string | null }) {
  const words = useCopy().text.channel.search;
  const navigate = useNavigate();
  const [value, setValue] = useState(q ?? '');

  return (
    <GridSearch
      id="search-channel"
      value={value}
      onChange={setValue}
      words={words}
      onSubmit={() => {
        const next = value.trim();
        // Submitting an emptied box removes an applied search — backspacing alone edits the
        // text and changes nothing until Enter or the icon says so.
        if (next === '') {
          if (q !== null) navigate(channelClearSearchHref(channelId));
          return;
        }
        if (next !== q) navigate(channelSearchHref(channelId, next));
      }}
      // The ✕ undoes the *applied* search in one press; with none applied it only empties.
      onClear={q === null ? undefined : () => navigate(channelClearSearchHref(channelId))}
    />
  );
}

/**
 * The banner, the avatar and the identity line.
 *
 * The banner box owns its aspect ratio rather than taking it from the record: `authorBanners` declares
 * four sizes for one URL and calls the last of them 512×288, for an image that is actually 2176×360.
 * `object-fit: cover` on a box we chose is the only rendering that cannot be wrong.
 */
function ChannelHead({
  binding,
  channelId,
  search,
}: {
  readonly binding: ChannelBinding;
  readonly channelId: string;
  readonly search: string | null;
}) {
  const words = useCopy().text.channel.page;
  const header = binding.header;
  const navigate = useNavigate();
  const [sortOpen, setSortOpen] = useState(false);

  return (
    <header className={styles.head}>
      {header?.bannerUrl === undefined || header.bannerUrl === null ? null : (
        <div className={styles.banner}>
          <img className={styles.bannerImage} src={header.bannerUrl} alt="" decoding="async" />
        </div>
      )}

      <div className={styles.identity}>
        <span className={styles.avatar}>
          {header === null || header.avatarUrl === null ? (
            <span className={styles.avatarBlank} aria-hidden="true">
              {(header?.name ?? '').slice(0, 1)}
            </span>
          ) : (
            <img className={styles.avatarImage} src={header.avatarUrl} alt="" decoding="async" />
          )}
        </span>

        <div className={styles.identityText}>
          <h1 className={styles.name}>
            {/* The UCID while the record is in flight: it is what the reader typed or clicked, and an
                empty heading that fills in a moment later shifts the whole page. */}
            {header === null ? <span className={styles.pending}>{channelId}</span> : header.name}
            {header?.verified === true ? (
              <span className={styles.verified} title={words.verified}>
                ✓
              </span>
            ) : null}
            {binding.refreshing ? (
              <span className={styles.refreshing}>
                <Spinner />
              </span>
            ) : null}
          </h1>

          <p className={styles.meta}>
            {header?.subscriberText === undefined || header.subscriberText === null ? null : (
              <span title={header.subscriberTitle ?? undefined}>
                {words.subscribers(header.subscriberText)}
              </span>
            )}
            {(header?.badges ?? []).map((badge) => (
              <span className={styles.badge} key={badge}>
                {badge}
              </span>
            ))}
          </p>
        </div>

        {/* The sub/unsub button, beside the identity. The route's id is unvalidated here — the
            button refuses anything that is not a UCID itself. */}
        <div className={styles.subscribe}>
          <SubscribeButton channelId={channelId} />
        </div>
      </div>

      <div className={styles.controls}>
        {/* Tabs are `<Link>`s, not buttons: they are destinations, and the back button has to be what
            "go back to Videos" means. `aria-current` rather than a class alone, because that is the
            fact a screen reader needs. */}
        <nav className={styles.tabs} aria-label={words.tabsLabel}>
          {binding.tabs.map((link) => (
            <Link
              key={link.tab}
              className={link.current ? styles.tabCurrent : styles.tab}
              href={link.href}
              ariaCurrent={link.current}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className={styles.controlsEnd}>
          {/* The controls line's right half (CLAUDE.md): search, then sort. The search is
              keyed by the route's `q`, so a new search reseeds the box. Absent on About —
              searching *videos* from the tab that has none reads as searching the description. It
              stays while results are on screen, since that page is what the ✕ clears. */}
          {search === null && binding.tab === 'about' ? null : (
            <ChannelSearchControl key={search ?? ''} channelId={channelId} q={search} />
          )}

          {/*
            * Absent on Playlists and About, and that is a measurement rather than tidiness: on
            * `/playlists` an unrecognised `sort_by` answers `200 []`, so offering "Most viewed" there
            * would empty a channel's playlists and read as the channel having none. Absent while
            * searching too — the search endpoint ignores `sort_by` outright (§7f).
            *
            * A menu that navigates, the same shape as 2c's filter row — the sort is in the URL, so
            * choosing one is a navigation and the back button is what undoing it means.
            */}
          {binding.sortMenu === null ? null : (
            <Menu
              menu={binding.sortMenu}
              open={sortOpen}
              onOpenChange={setSortOpen}
              onSelect={(itemId) => {
                setSortOpen(false);
                navigate(sortHref(channelId, binding.tab, itemId));
              }}
            />
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * The name of the thing that could not be loaded, so the panel below has a subject. It is the UCID
 * because that is all there is — the record that would have carried a name is the one that failed.
 */
function FailedHead({ channelId }: { readonly channelId: string }) {
  return (
    <header className={styles.head}>
      <h1 className={styles.name}>
        <span className={styles.pending}>{channelId}</span>
      </h1>
    </header>
  );
}

/** The channel record itself failing — there is no header, no tab strip and nothing to page. */
function ChannelProblem({ binding }: { readonly binding: ChannelBinding }) {
  const copy = binding.problem;
  if (copy === null) return null;
  return <ProblemPanel copy={copy} onRetry={binding.retry} />;
}

function TabBody({ binding, search }: { readonly binding: ChannelBinding; readonly search: string | null }) {
  const copy = useCopy().text.channel;
  const words = copy.page;
  const state = search === null && binding.tab === 'about' ? null : binding.list;
  // Unconditional (rules of hooks) — inert on About and while there is nothing further to page.
  const autoLoad = useAutoLoadMore(
    state?.kind === 'ready' && state.loadMore === 'available',
    binding.loadMore,
  );
  if (search === null && binding.tab === 'about') return <About binding={binding} />;

  if (state === null) return null;

  return (
    <section className={styles.list} aria-busy={state.kind === 'loading' ? true : undefined}>
      {/* What the list below is, since no tab is current while searching. */}
      {search === null ? null : <h2 className={styles.searchHeading}>{copy.search.resultsFor(search)}</h2>}

      {state.kind === 'loading' ? (
        <>
          <LoadingStatus>{search === null ? words.loadingTab(binding.tab) : copy.search.loading}</LoadingStatus>
          <FeedGridSkeleton cards={state.skeletonCards} />
        </>
      ) : null}

      {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

      {state.kind === 'empty' ? <StatePanel title={state.title} detail={state.detail} /> : null}

      {state.kind === 'ready' ? (
        <>
          {state.notices.map((notice) => (
            <Notice key={notice.id} tone="caution">
              {notice.text}
            </Notice>
          ))}
          <ListingGrid
            rows={state.rows}
            label={search === null ? words.gridLabel(binding.tab) : copy.search.gridLabel}
          />

          {/*
           * A button that the observer presses, rather than a numbered pager. The cursor is a 4 KB
           * opaque token, so there is no page 5 to link to and no total to count against — the
           * *only* end-of-list signal is the token being absent, and a finished list simply ends —
           * the "that is all N" sentence is retired.
           */}
          <div className={styles.paging} ref={autoLoad}>
            {state.loadMore === null ? null : (
              <Button onClick={binding.loadMore} disabled={state.loadMore === 'loading'}>
                {state.loadMore === 'loading' ? words.loadingMore : words.loadMore(state.loaded)}
              </Button>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

/** The About tab's "View on" dropdown — its open state and nothing else. */
function JumpOutMenu({ menu }: { readonly menu: MenuModel }) {
  const [open, setOpen] = useState(false);
  return (
    <Menu
      menu={menu}
      variant="pill"
      triggerIcon={<JumpOutIcon />}
      open={open}
      onOpenChange={setOpen}
      // The item is a link and the browser is already following it — all that is left is closing.
      onSelect={() => setOpen(false)}
    />
  );
}

/**
 * The About tab, which is shorter than §8 implies and the gap is measured: the response has **no
 * `links` field**, and `totalViews` and `joined` are `0` on every channel probed. So the description is
 * the substance, and the missing figures are stated rather than invented from their zeros.
 */
function About({ binding }: { readonly binding: ChannelBinding }) {
  const words = useCopy().text.channel.page;
  const about = binding.about;
  if (about === null) {
    /*
     * This used to say "Loading…" in muted text, which was the only place in the app where a loading
     * state was a *word* rather than the shape of what is coming — and the shape matters here because
     * the description is the tab's whole substance: four lines arriving under one short word moves
     * everything below them.
     */
    return (
      <section className={styles.about} aria-busy>
        <LoadingStatus>{words.loadingAbout}</LoadingStatus>
        <div className={styles.aboutSkeleton} aria-hidden="true">
          <Skeleton text width="100%" />
          <Skeleton text width="96%" />
          <Skeleton text width="88%" />
          <Skeleton text width="42%" />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.about}>
      {about.hasDescription ? (
        <RichText className={styles.description} nodes={about.descriptionNodes} />
      ) : (
        <p className={styles.aboutEmpty}>{words.noDescription}</p>
      )}

      {/* §8's jump-out, where the subscriber figure used to repeat the header — one "View on"
          dropdown since the merge, the watch page's shape. The items are real <a>s and `Menu` sends
          them out with no referrer. */}
      <div className={styles.jumpOut}>
        <JumpOutMenu menu={about.jumpOut} />
      </div>

      {about.totalViewsText === null ? null : (
        <dl className={styles.facts}>
          <dt className={styles.factName}>{words.totalViewsLabel}</dt>
          <dd className={styles.factValue}>{about.totalViewsText}</dd>
        </dl>
      )}

      {about.tags.length === 0 ? null : (
        <div className={styles.tags}>
          <h2 className={styles.tagsHeading}>{words.keywords}</h2>
          <ul className={styles.tagList}>
            {about.tags.map((tag) => (
              <li className={styles.tag} key={tag}>
                {tag}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
